import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CONTACT_INFO, VALIDATION_MESSAGES, POLITICAL_POSITIONS, APP_CONFIG } from '../constants';
import {
  trackFormStart,
  trackFieldInteraction,
  trackValidationError,
  trackSubmissionAttempt,
  trackSubmissionSuccess,
  trackSubmissionFailure,
  trackWhatsAppClick,
  trackModalInteraction
} from '../utils/formAnalytics';

const ClientOnboardingModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    fullName: '',
    politicalPosition: '',
    constituencyRegion: '',
    whatsappNumber: '',
    targetElection: ''
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    error: null
  });

  const [formErrors, setFormErrors] = useState({});

  // Reset form when modal opens and track modal interactions
  useEffect(() => {
    if (isOpen) {
      // Track modal opening and form start
      trackModalInteraction('open', 'client_onboarding');
      trackFormStart('client_onboarding', 'modal');

      setFormData({
        fullName: '',
        politicalPosition: '',
        constituencyRegion: '',
        whatsappNumber: '',
        targetElection: ''
      });
      setFormStatus({ isSubmitting: false, isSubmitted: false, error: null });
      setFormErrors({});
    }
  }, [isOpen]);

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    // Track field interaction
    trackFieldInteraction('client_onboarding', name, 'change', value);

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error for this field when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  }, [formErrors]);

  const validateForm = useCallback(() => {
    const errors = {};

    if (!formData.fullName.trim()) {
      errors.fullName = VALIDATION_MESSAGES.REQUIRED_FIELD;
    }

    if (!formData.politicalPosition.trim()) {
      errors.politicalPosition = VALIDATION_MESSAGES.REQUIRED_FIELD;
    }

    if (!formData.constituencyRegion.trim()) {
      errors.constituencyRegion = VALIDATION_MESSAGES.REQUIRED_FIELD;
    }

    if (!formData.whatsappNumber.trim()) {
      errors.whatsappNumber = VALIDATION_MESSAGES.REQUIRED_FIELD;
    } else if (!/^[\+]?[1-9][\d]{0,15}$/.test(formData.whatsappNumber.replace(/\s/g, ''))) {
      errors.whatsappNumber = VALIDATION_MESSAGES.INVALID_PHONE;
    }

    if (!formData.targetElection.trim()) {
      errors.targetElection = VALIDATION_MESSAGES.REQUIRED_FIELD;
    }

    return errors;
  }, [formData]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();

    // Validate form
    const errors = validateForm();
    const isValid = Object.keys(errors).length === 0;

    // Track submission attempt
    trackSubmissionAttempt('client_onboarding', isValid,
      Object.keys(errors).map(field => ({ field, type: 'validation' }))
    );

    if (!isValid) {
      setFormErrors(errors);
      // Track validation errors
      Object.keys(errors).forEach(field => {
        trackValidationError('client_onboarding', field, errors[field]);
      });
      return;
    }

    setFormStatus({ isSubmitting: true, isSubmitted: false, error: null });

    try {
      // Prepare data for Laravel API
      const apiData = {
        name: formData.fullName,
        political_position: formData.politicalPosition,
        constituency: formData.constituencyRegion,
        whatsapp_number: formData.whatsappNumber,
        target_election: formData.targetElection,
        phone: formData.whatsappNumber, // Use WhatsApp number as phone
        source: 'client_onboarding'
      };

      // Submit to Laravel API with timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), APP_CONFIG.FORM_TIMEOUT);

      const response = await fetch(`${APP_CONFIG.API_BASE_URL}/api/enquiries`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
        },
        body: JSON.stringify(apiData),
        signal: controller.signal
      });

      clearTimeout(timeoutId);

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to submit enquiry');
      }

      // Log only in development
      if (process.env.NODE_ENV === 'development') {
        console.log('Onboarding form submitted:', result);
      }

      // Track successful submission
      trackSubmissionSuccess('client_onboarding', apiData);

      setFormStatus({ isSubmitting: false, isSubmitted: true, error: null });

      // Close modal and show success after delay
      setTimeout(() => {
        trackModalInteraction('close', 'client_onboarding');
        onClose();
        setFormData({
          fullName: '',
          politicalPosition: '',
          constituencyRegion: '',
          whatsappNumber: '',
          targetElection: ''
        });
        setFormStatus({ isSubmitting: false, isSubmitted: false, error: null });
      }, APP_CONFIG.FORM_RESET_DELAY);

    } catch (error) {
      // Log only in development
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting onboarding form:', error);
      }

      let errorMessage = VALIDATION_MESSAGES.FORM_ERROR;
      let errorType = 'unknown';

      if (error.name === 'AbortError') {
        errorMessage = 'Request timed out. Please check your connection and try again.';
        errorType = 'timeout';
      } else if (error.message.includes('Failed to fetch')) {
        errorMessage = 'Unable to connect to server. Please check your internet connection.';
        errorType = 'network';
      } else if (error.message) {
        errorMessage = error.message;
        errorType = 'server';
      }

      // Track submission failure
      trackSubmissionFailure('client_onboarding', errorType, errorMessage);

      setFormStatus({
        isSubmitting: false,
        isSubmitted: false,
        error: errorMessage
      });
    }
  }, [formData, validateForm]);

  // Handle WhatsApp click with analytics tracking
  const handleWhatsAppClick = useCallback(() => {
    // Track WhatsApp click
    trackWhatsAppClick('client_onboarding', formData);

    const message = `Hi! I'm interested in political consultation services. My name is ${formData.fullName || '[Name]'} and I'm a ${formData.politicalPosition || '[Position]'} from ${formData.constituencyRegion || '[Constituency]'}.`;
    const whatsappUrl = `${CONTACT_INFO.WHATSAPP_URL}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  }, [formData]);

  // Handle modal close with analytics tracking
  const handleModalClose = useCallback(() => {
    trackModalInteraction('close', 'client_onboarding');
    onClose();
  }, [onClose]);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with blur */}
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleModalClose}
          />

          {/* Modal */}
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.3 }}
          >
            {/* Modal Content */}
            <div className="relative w-full max-w-2xl mx-auto bg-white rounded-2xl shadow-2xl overflow-hidden">
              {/* Header with patriotic gradient */}
              <div className="bg-gradient-to-r from-orange-500 via-white to-green-500 p-1">
                <div className="bg-white rounded-t-xl">
                  <div className="flex justify-between items-center p-6 pb-4">
                    <div className="text-center flex-1">
                      <div className="inline-flex items-center space-x-3 bg-jetBlue-100 text-jetBlue-800 rounded-full px-4 py-2 mb-2">
                        <span className="text-lg">🚀</span>
                        <span className="font-semibold text-sm tracking-wide">CLIENT ONBOARDING</span>
                      </div>
                      <h2 className="text-2xl font-bold text-gray-900">
                        Start Your Political Journey
                      </h2>
                      <p className="text-gray-600 text-sm mt-1">
                        Join 5,500+ successful politicians across India
                      </p>
                    </div>
                    <button
                      onClick={handleModalClose}
                      className="text-gray-400 hover:text-gray-600 transition-colors p-2 hover:bg-gray-100 rounded-full"
                    >
                      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                      </svg>
                    </button>
                  </div>
                </div>
              </div>

              <div className="p-6 pt-4">

                  <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="fullName"
                        value={formData.fullName}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          formErrors.fullName ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="Your full name"
                      />
                      {formErrors.fullName && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.fullName}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Political Position *</label>
                      <select
                        name="politicalPosition"
                        value={formData.politicalPosition}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          formErrors.politicalPosition ? 'border-red-500' : 'border-gray-300'
                        }`}
                      >
                        {POLITICAL_POSITIONS.map((position) => (
                          <option key={position.value} value={position.value}>
                            {position.label}
                          </option>
                        ))}
                      </select>
                      {formErrors.politicalPosition && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.politicalPosition}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Constituency/Region *</label>
                      <input
                        type="text"
                        name="constituencyRegion"
                        value={formData.constituencyRegion}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          formErrors.constituencyRegion ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="e.g., Bhopal, Mumbai North, Patna Sahib"
                        maxLength="100"
                      />
                      {formErrors.constituencyRegion && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.constituencyRegion}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">WhatsApp Number *</label>
                      <input
                        type="tel"
                        name="whatsappNumber"
                        value={formData.whatsappNumber}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          formErrors.whatsappNumber ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="+91 98765 43210"
                        maxLength="15"
                      />
                      {formErrors.whatsappNumber && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.whatsappNumber}</p>
                      )}
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Target Election *</label>
                      <input
                        type="text"
                        name="targetElection"
                        value={formData.targetElection}
                        onChange={handleInputChange}
                        required
                        className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                          formErrors.targetElection ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="e.g., 2024 Lok Sabha, 2025 Assembly Elections, Local Body 2024"
                        maxLength="100"
                      />
                      {formErrors.targetElection && (
                        <p className="text-red-500 text-sm mt-1">{formErrors.targetElection}</p>
                      )}
                    </div>

                    <div className="md:col-span-2 mt-6">
                      <button
                        type="submit"
                        disabled={formStatus.isSubmitting}
                        className={`w-full font-bold py-4 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 ${
                          formStatus.isSubmitting
                            ? 'bg-gray-400 cursor-not-allowed scale-100'
                            : formStatus.isSubmitted
                            ? 'bg-green-600 hover:bg-green-700'
                            : 'bg-gradient-to-r from-orange-500 via-red-500 to-green-500 hover:from-orange-600 hover:via-red-600 hover:to-green-600 shadow-lg'
                        } text-white`}
                      >
                        {formStatus.isSubmitting ? (
                          <span className="flex items-center justify-center">
                            <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            Submitting Request...
                          </span>
                        ) : formStatus.isSubmitted ? (
                          <span className="flex items-center justify-center">
                            <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Onboarding Complete!
                          </span>
                        ) : (
                          'Start Your Political Journey →'
                        )}
                      </button>
                    </div>

                    {/* Success/Error Messages */}
                    {formStatus.isSubmitted && (
                      <div className="md:col-span-2 mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                        <p className="text-green-800 text-sm">
                          ✅ Welcome to Jet Digital! Our team will contact you within 24 hours to begin your political transformation journey.
                        </p>
                      </div>
                    )}

                    {formStatus.error && (
                      <div className="md:col-span-2 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                        <p className="text-red-800 text-sm">
                          ❌ {formStatus.error}
                        </p>
                      </div>
                    )}

                    {/* WhatsApp Alternative */}
                    <div className="md:col-span-2 mt-4 pt-4 border-t border-gray-200">
                      <p className="text-center text-sm text-gray-600 mb-3">
                        Need immediate assistance?
                      </p>
                      <button
                        type="button"
                        onClick={handleWhatsAppClick}
                        className="w-full bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center space-x-2"
                      >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.097"/>
                        </svg>
                        <span>Connect via WhatsApp</span>
                      </button>
                    </div>
                  </form>
                </div>
              </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default ClientOnboardingModal;
